/**
 * BouncingPointer — web port of
 * client-native/src/features/tutorial/components/BouncingPointer.tsx.
 *
 * A pointing-hand PNG (iOS click-word.png) that bounces ~12px toward the
 * gesture target, on a loop (400ms out, 400ms back, 200ms pause), to draw the
 * eye to the demo gesture. The asset points up at 0deg and is rotated per
 * direction, matching iOS.
 */
import { CSSProperties, FC } from "react";
import pointerImage from "../assets/click-word.png";

type PointerDirection = "right" | "up" | "down" | "left";

interface BouncingPointerProps {
  size?: number;
  isAnimating?: boolean;
  direction?: PointerDirection;
  style?: CSSProperties;
}

// Emoji 👆 points up at 0deg. Rotation map mirrors iOS ROTATION_MAP.
const ROTATION: Record<PointerDirection, number> = {
  up: 0,
  right: 90,
  down: -90,
  left: 270,
};

// Bounce offset expressed in the ROTATED coordinate frame (the translate runs
// after the rotate in `rotate(...) translate(...)`), matching iOS, so the
// pointer moves toward the on-screen target regardless of facing direction.
//   right: rotate 90deg  → translateY -12 ⇒ screen +X (right)
//   left:  rotate 270deg → translateY +12 ⇒ screen -X (left)
//   up:    rotate 0deg   → translateY -12 ⇒ screen -Y (up)
//   down:  rotate -90deg → translateY +12 ⇒ screen +Y (down)
const OFFSET: Record<PointerDirection, { x: number; y: number }> = {
  up: { x: 0, y: -12 },
  down: { x: 0, y: 12 },
  left: { x: 0, y: 12 },
  right: { x: 0, y: -12 },
};

const BouncingPointer: FC<BouncingPointerProps> = ({
  size = 80,
  isAnimating = true,
  direction = "right",
  style,
}) => {
  const rot = ROTATION[direction];
  const { x, y } = OFFSET[direction];
  const animName = `bounce-${direction}`;

  return (
    <>
      <style>{`
        @keyframes ${animName} {
          0% { transform: rotate(${rot}deg) translate(0px, 0px); }
          40% { transform: rotate(${rot}deg) translate(${x}px, ${y}px); }
          80% { transform: rotate(${rot}deg) translate(0px, 0px); }
          100% { transform: rotate(${rot}deg) translate(0px, 0px); }
        }
      `}</style>
      <img
        src={pointerImage}
        alt=""
        aria-hidden
        style={{
          display: "inline-block",
          width: size,
          height: size,
          objectFit: "contain",
          animation: isAnimating ? `${animName} 1000ms ease-in-out infinite` : undefined,
          transform: `rotate(${rot}deg)`,
          ...style,
        }}
      />
    </>
  );
};

export default BouncingPointer;
